import React, {
    useEffect,
    useId,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { useLatest } from 'ahooks';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { SendHorizontalIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import { Form, FormField, FormItem, FormMessage } from '@rocco/components/form';
import { TOAST_CLOSE_BUTTON } from '~/utils/toast';
import { renameFile } from '~/modules/file';
import type { TextOrFileMessage } from '../../types';
import { useConversation } from '../../context';
import { useChatMentions } from '../../hooks/use-chat-mentions';
import { useSendTypingIndicator } from '../../hooks/use-typing-indicator';
import {
    validateChatUploadFile,
    validateChatUploadFileCount,
} from '../../utils/validation';
import { useChatInputDraft } from '../../hooks/use-chat-input-draft';
import {
    ChatInputComposer,
    type ChatInputComposerApi,
} from './ChatInputComposer';
import { ChatInputReplyPreview } from './ChatInputReplyPreview';
import { ChatInputAttachments } from './ChatInputAttachments';
import { ChatInputMentionDropdown } from './ChatInputMentionDropdown';
import { TypingIndicator } from './TypingIndicator';
import { GiphyPicker } from './GiphyPicker';

const formSchema = z.object({
    message: z
        .string()
        .trim()
        .max(2000, { message: 'Maximum 2000 characters' }),
    attachments: z.array(z.instanceof(File)),
});

/**
 * Imperative handle exposed by `ChatInput` for programmatic file additions.
 */
export interface ChatInputHandle {
    isFileUploadDisabled: boolean;
    /**
     * Add files as attachments. Invalid files will be rejected.
     */
    addFiles: (files: FileList) => void;
}

interface ChatInputProps {
    className?: string;
    /**
     * Whether the chat box is open.
     */
    isOpen: boolean;
    /**
     * Whether the form is submitting. Only used when sending a message with
     * file attachments. Defaults to `false`.
     */
    isSubmitting?: boolean;
    /**
     * Auto focus the input when the chat box is opened. Defaults to `true`.
     */
    autoFocus?: boolean;
    /**
     * The message being replied to or forwarded. If set, a preview of the
     * message is shown.
     */
    replyingTo?: TextOrFileMessage | null;
    /**
     * The mode of the reply or forward feature. Defaults to `'reply'`.
     */
    replyMode?: 'reply' | 'forward';
    /**
     * Callback when the user cancels the reply.
     */
    onCancelReply?: () => void;
    /**
     * Callback when the user sends a message.
     */
    onSend?: ({
        message,
        attachments,
        replyTo,
    }: {
        message: string;
        attachments: File[];
        replyTo?: TextOrFileMessage;
    }) => void;
    /**
     * Callback when the attachments have changed.
     */
    onAttachmentsChanged?: () => void;
}

/**
 * The text input for the chat.
 * - The chat text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 */
export const ChatInput = React.forwardRef<ChatInputHandle, ChatInputProps>(
    function ChatInput(
        {
            className,
            isOpen,
            isSubmitting = false,
            autoFocus = true,
            replyingTo,
            replyMode = 'reply',
            onCancelReply,
            onSend,
            onAttachmentsChanged,
        },
        ref,
    ) {
        const {
            conversationID,
            professionalID,
            conversation,
            isParticipant,
            isLoading,
        } = useConversation();
        const sendTypingIndicator = useSendTypingIndicator();
        const isDisabled = !conversation && (isLoading || conversationID > 0);
        const isForwarding = !!replyingTo && replyMode === 'forward';
        const formId = useId();
        const formRef = useRef<HTMLFormElement>(null);
        const composerRef = useRef<HTMLDivElement | null>(null);
        const composerApiRef = useRef<ChatInputComposerApi | null>(null);
        const isHandlingMentionNavigationRef = useRef(false);
        const skipNextMentionSelectionSyncRef = useRef(false);
        const fileInputRef = useRef<HTMLInputElement>(null);
        const isDraftHydratedRef = useRef(false);
        const [composerInstanceKey, setComposerInstanceKey] = useState(0);
        const onAttachmentsChangedRef = useLatest(onAttachmentsChanged);
        const onFilesAddedRef = useLatest(onFilesAdded);
        const {
            mentionParticipants,
            selectedMentionIndex,
            setSelectedMentionIndex,
            updateMentionContext,
            selectMention,
            resetMentions,
        } = useChatMentions({
            conversation,
            onSelectMention: (participant) => {
                composerApiRef.current?.insertMention(participant);
            },
        });
        const isMentionsDisabled =
            conversation?.__typename === 'EnquiryConversation';
        const isMentionOpen =
            !isMentionsDisabled && mentionParticipants.length > 0;
        const form = useForm<z.infer<typeof formSchema>>({
            resolver: zodResolver(formSchema),
            defaultValues: {
                message: '',
                attachments: [],
            },
        });
        const message = form.watch('message');
        const attachments = form.watch('attachments');
        const { draft, saveDraft, clearDraft } = useChatInputDraft(
            conversationID,
            professionalID,
        );
        // Disable file uploads when forwarding.
        const isFileUploadDisabled =
            isForwarding || isSubmitting || !isParticipant;
        const isSendDisabled =
            isDisabled ||
            isSubmitting ||
            !isParticipant ||
            // When forwarding, text and attachments are optional.
            (!message.trim() && !attachments.length && !isForwarding);

        useImperativeHandle(
            ref,
            () => ({
                isFileUploadDisabled,
                addFiles: (files) => {
                    onFilesAddedRef.current(files, false);
                },
            }),
            [isFileUploadDisabled, onFilesAddedRef],
        );

        /**
         * Handle adding file attachments to the message.
         * @param files The files to add.
         * @param renamePastedImages Rename pasted images to include a timestamp.
         */
        function onFilesAdded(
            files: Iterable<File> | null,
            renamePastedImages: boolean,
        ) {
            const filesToAdd = files ? Array.from(files) : [];
            if (!filesToAdd.length || isFileUploadDisabled) {
                return;
            }
            // TODO: tracker
            const newAttachments = [...form.getValues('attachments')];
            const errors = new Set<string>();
            for (const file of filesToAdd) {
                const isDuplicate = newAttachments.some(
                    (f) =>
                        f.name === file.name &&
                        f.size === file.size &&
                        f.lastModified === file.lastModified,
                );
                if (isDuplicate) {
                    continue;
                }
                const isFileCountValid = validateChatUploadFileCount(
                    newAttachments.length + 1,
                );
                if (typeof isFileCountValid === 'object') {
                    errors.add(isFileCountValid.message);
                    break;
                }
                const validationResult = validateChatUploadFile(file);
                if (validationResult.valid) {
                    // Pasted images have the filename "image.png". Rename them to
                    // be more descriptive.
                    if (renamePastedImages && file.name === 'image.png') {
                        newAttachments.push(renamePastedImage(file));
                    } else {
                        newAttachments.push(file);
                    }
                } else {
                    errors.add(validationResult.message);
                }
            }
            form.setValue('attachments', newAttachments);
            for (const error of errors) {
                toast.error(error, TOAST_CLOSE_BUTTON);
            }
            composerApiRef.current?.focus();
            onAttachmentsChanged?.();
        }

        function onAttachmentRemove(index: number) {
            form.setValue(
                'attachments',
                attachments.filter((_, i) => i !== index),
            );
            composerApiRef.current?.focus();
            onAttachmentsChanged?.();
        }

        function onSubmit(values: z.infer<typeof formSchema>) {
            if (isSendDisabled) {
                return;
            }
            onSend?.({
                message: values.message,
                // Attachments not allowed when forwarding.
                attachments: isForwarding ? [] : values.attachments,
                replyTo: replyingTo ?? undefined,
            });
            clearDraft();
            form.reset();
            setComposerInstanceKey((value) => value + 1);
            requestAnimationFrame(() => {
                composerRef.current?.focus({ preventScroll: true });
            });
        }

        // Restore any saved draft before auto-saving form state.
        useLayoutEffect(() => {
            if (isDraftHydratedRef.current) {
                return;
            }
            const hasFormContent = !!message || attachments.length > 0;
            const hasDraftContent =
                !!draft.message || draft.attachments.length > 0;
            if (!hasFormContent && hasDraftContent) {
                setTimeout(() => {
                    form.setValue('message', draft.message);
                    saveDraft({
                        message: draft.message,
                        attachments: draft.attachments,
                    });
                }, 0);
                form.setValue('attachments', draft.attachments);
                onAttachmentsChangedRef.current?.();
            }
            isDraftHydratedRef.current = true;
        }, [
            draft,
            form,
            message,
            attachments,
            onAttachmentsChangedRef,
            saveDraft,
        ]);

        // Keep draft state synced with current form values.
        useEffect(() => {
            if (!isDraftHydratedRef.current) {
                return;
            }
            saveDraft({ message, attachments });
        }, [message, attachments, saveDraft]);

        // Reset the draft when the conversation context changes.
        useEffect(() => {
            isDraftHydratedRef.current = false;
        }, [conversationID, professionalID]);

        // Reset the attachments when forwarding is toggled.
        useEffect(() => {
            if (isForwarding && attachments.length) {
                form.setValue('attachments', []);
            }
        }, [isForwarding, attachments.length, form]);

        // Focus the input when the chat box is opened.
        useEffect(() => {
            if (isOpen && autoFocus) {
                requestAnimationFrame(() => {
                    composerApiRef.current?.focus();
                });
            }
        }, [isOpen, autoFocus]);

        // Focus the input when reply mode is toggled.
        useEffect(() => {
            if (autoFocus) {
                composerApiRef.current?.focus();
            }
        }, [replyingTo, autoFocus]);

        return (
            <Form {...form}>
                <form
                    ref={formRef}
                    id={`chat-box-input-${formId}`}
                    className={cn('flex flex-col gap-[8px]', className)}
                    onSubmit={form.handleSubmit(onSubmit)}
                >
                    {replyingTo && (
                        <ChatInputReplyPreview
                            message={replyingTo}
                            mode={replyMode}
                            onCancel={onCancelReply}
                        />
                    )}
                    <FormField
                        name="message"
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className="relative space-y-[8px]">
                                <ChatInputComposer
                                    key={composerInstanceKey}
                                    ref={(el) => {
                                        field.ref(el);
                                        composerRef.current = el;
                                    }}
                                    composerApiRef={composerApiRef}
                                    message={field.value}
                                    placeholder={
                                        isParticipant
                                            ? 'Write a message...'
                                            : 'Signed in as ArchiPro staff - sending is disabled'
                                    }
                                    autoFocus={autoFocus}
                                    isDisabled={
                                        field.disabled || !isParticipant
                                    }
                                    onChange={(text) => {
                                        field.onChange(text);
                                        if (text.length > field.value.length) {
                                            sendTypingIndicator();
                                        }
                                    }}
                                    onSelectionChange={({
                                        text,
                                        selectionStart,
                                    }) => {
                                        if (
                                            isHandlingMentionNavigationRef.current ||
                                            skipNextMentionSelectionSyncRef.current
                                        ) {
                                            skipNextMentionSelectionSyncRef.current = false;
                                            return;
                                        }
                                        updateMentionContext(
                                            text,
                                            selectionStart,
                                        );
                                    }}
                                    onKeyDown={(e) => {
                                        if (isMentionOpen) {
                                            if (e.key === 'ArrowDown') {
                                                isHandlingMentionNavigationRef.current = true;
                                                skipNextMentionSelectionSyncRef.current = true;
                                                e.preventDefault();
                                                setSelectedMentionIndex(
                                                    (value) =>
                                                        (value + 1) %
                                                        mentionParticipants.length,
                                                );
                                                return;
                                            }
                                            if (e.key === 'ArrowUp') {
                                                isHandlingMentionNavigationRef.current = true;
                                                skipNextMentionSelectionSyncRef.current = true;
                                                e.preventDefault();
                                                setSelectedMentionIndex(
                                                    (value) =>
                                                        (value -
                                                            1 +
                                                            mentionParticipants.length) %
                                                        mentionParticipants.length,
                                                );
                                                return;
                                            }
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === 'Tab'
                                            ) {
                                                isHandlingMentionNavigationRef.current = true;
                                                skipNextMentionSelectionSyncRef.current = true;
                                                e.preventDefault();
                                                const selectedMention =
                                                    mentionParticipants[
                                                        selectedMentionIndex
                                                    ];
                                                if (selectedMention) {
                                                    selectMention(
                                                        selectedMention,
                                                    );
                                                }
                                                return;
                                            }
                                            if (e.key === 'Escape') {
                                                isHandlingMentionNavigationRef.current = true;
                                                skipNextMentionSelectionSyncRef.current = true;
                                                e.preventDefault();
                                                resetMentions();
                                                return;
                                            }
                                        }
                                        if (e.key === 'Enter') {
                                            if (
                                                e.ctrlKey ||
                                                e.altKey ||
                                                e.shiftKey
                                            ) {
                                                // Ctrl, Alt, or Shift + Enter to insert a new line.
                                                return;
                                            }
                                            // Just Enter to send the message.
                                            e.preventDefault();
                                            formRef.current?.requestSubmit();
                                        }
                                    }}
                                    onKeyUp={(e, text, selectionStart) => {
                                        const handledMentionNavigation =
                                            isHandlingMentionNavigationRef.current;
                                        isHandlingMentionNavigationRef.current = false;

                                        if (
                                            handledMentionNavigation ||
                                            e.key === 'Escape' ||
                                            (isMentionOpen &&
                                                (e.key === 'ArrowDown' ||
                                                    e.key === 'ArrowUp' ||
                                                    e.key === 'Enter' ||
                                                    e.key === 'Tab'))
                                        ) {
                                            // Prevent the normal key up behaviour
                                            // from messing with the mention popup.
                                            return;
                                        }
                                        // Update mention context when selection
                                        // changes (via arrow keys).
                                        skipNextMentionSelectionSyncRef.current = false;
                                        updateMentionContext(
                                            text,
                                            selectionStart,
                                        );
                                    }}
                                    onPaste={(e) => {
                                        if (isForwarding) return;
                                        const files = getClipboardFiles(
                                            e.clipboardData,
                                        );
                                        if (files.length) {
                                            e.preventDefault();
                                            onFilesAdded(files, true);
                                        }
                                    }}
                                    onBlur={resetMentions}
                                />
                                {isMentionOpen && (
                                    <ChatInputMentionDropdown
                                        className="absolute inset-x-0 bottom-[calc(100%+4px)] z-1"
                                        participants={mentionParticipants}
                                        selectedMentionIndex={
                                            selectedMentionIndex
                                        }
                                        onSelectMention={selectMention}
                                    />
                                )}
                                <FormMessage
                                    className="text-[12px]"
                                    variant="darker"
                                />
                            </FormItem>
                        )}
                    />
                    {attachments.length > 0 && (
                        <ChatInputAttachments
                            attachments={attachments}
                            onAttachmentRemove={onAttachmentRemove}
                        />
                    )}
                    <TypingIndicator className="px-[12px]" />
                    <div className="flex items-center justify-between gap-[8px]">
                        <div className="flex items-center gap-[4px]">
                            {/* Attachments not allowed when forwarding. */}
                            {!isForwarding && (
                                <>
                                    <Button
                                        className="border border-[#474747] py-2 leading-tight text-[#474747] hover:bg-black/10"
                                        type="button"
                                        color="transparent"
                                        size="narrow"
                                        weight="normal"
                                        disabled={isFileUploadDisabled}
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                    >
                                        Upload File
                                    </Button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="hidden"
                                        multiple
                                        onChange={(e) =>
                                            onFilesAdded(e.target.files, false)
                                        }
                                    />
                                </>
                            )}
                            <GiphyPicker
                                onSelect={(_, url) => {
                                    onSend?.({
                                        message: url,
                                        attachments: [],
                                        replyTo: replyingTo ?? undefined,
                                    });
                                }}
                            >
                                <Button
                                    className="border border-[#474747] py-2 leading-tight text-[#474747] hover:bg-black/10"
                                    type="button"
                                    color="transparent"
                                    size="narrow"
                                    weight="normal"
                                    disabled={isSubmitting || !isParticipant}
                                >
                                    GIF
                                </Button>
                            </GiphyPicker>
                        </div>
                        <Button
                            className="py-2 leading-tight"
                            type="submit"
                            color="dark"
                            size="larger"
                            disabled={isSendDisabled}
                            isLoading={isSubmitting}
                        >
                            {isForwarding ? 'Forward' : 'Send'}
                            <SendHorizontalIcon className="ml-2 size-4 fill-white text-white" />
                        </Button>
                    </div>
                </form>
            </Form>
        );
    },
);

/**
 * Rename a pasted file to include a timestamp, since browsers give pasted
 * images the generic name "image.png".
 * @example "image.png" -> "pasted-image-20260213-143022.png"
 */
function renamePastedImage(original: File): File {
    if (original.name !== 'image.png') {
        return original;
    }
    const now = new Date();
    const timestamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
        '-',
        String(now.getHours()).padStart(2, '0'),
        String(now.getMinutes()).padStart(2, '0'),
        String(now.getSeconds()).padStart(2, '0'),
    ].join('');
    const newName = `pasted-image-${timestamp}.png`;
    return renameFile(original, newName);
}

function getClipboardFiles(clipboardData: DataTransfer | null): File[] {
    if (!clipboardData) {
        return [];
    }

    if (clipboardData.files.length > 0) {
        return Array.from(clipboardData.files);
    }

    return Array.from(clipboardData.items)
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);
}
